// Profile › Servers (servers-and-developers WEB-SRV-LST-01 to -03, VisionPlatform app-server-selection SRV-01, SRV-02,
// SRV-27): the list of Creavision servers, read from the app's own origin, each linked by its `web` origin.
import { queryOptions } from "@tanstack/react-query";
import type { AppEnvironment } from "../config";
import type { Server } from "./generated/servers";

export type { Server };

/** Same-origin, so the CSP's connect-src stays as it is (WEB-SRV-LST-03, SRV-01). */
export const SERVERS_PATH = "/.well-known/creavision/servers.json";

const KINDS: readonly Server["kind"][] = ["production", "staging", "development"];

/** The `kind` in servers.json that config.json's `environment` names. */
export const KIND_OF: Record<AppEnvironment, Server["kind"]> = {
  production: "production",
  staging: "staging",
  dev: "development",
};

/** Servers is in the user menu for a developer, and always on a server that is not production (WEB-SRV-LST-01). */
export function showServers(developer: boolean | undefined, environment: AppEnvironment | undefined): boolean {
  return developer === true || environment === "staging" || environment === "dev";
}

/**
 * Whether `web` is an `https://` origin, no path, whose host is `creavision.tech` or ends in `.creavision.tech`
 * (WEB-SRV-LST-03, SRV §6), so a list that names another host never links there.
 */
export function isCreavisionOrigin(web: unknown): web is string {
  if (typeof web !== "string") return false;
  let url: URL;
  try {
    url = new URL(web);
  } catch {
    return false;
  }
  const host = url.hostname;
  return (
    url.protocol === "https:" &&
    url.origin === web.replace(/\/$/, "") &&
    !url.username &&
    !url.password &&
    (host === "creavision.tech" || host.endsWith(".creavision.tech"))
  );
}

/** The servers of a servers.json document the web app may link to; anything else is left out, never guessed. */
export function parseServers(raw: unknown): Server[] {
  if (typeof raw !== "object" || raw === null) throw new Error("servers.json is not an object");
  const list = (raw as { servers?: unknown }).servers;
  if (!Array.isArray(list)) throw new Error("servers.json has no servers");
  return list.flatMap((s: unknown): Server[] => {
    if (typeof s !== "object" || s === null) return [];
    const r = s as Record<string, unknown>;
    if (typeof r.id !== "string" || typeof r.label !== "string" || !r.label) return [];
    if (!KINDS.includes(r.kind as Server["kind"]) || !isCreavisionOrigin(r.web)) return [];
    const web = new URL(r.web).origin;
    return [
      {
        id: r.id,
        label: r.label,
        kind: r.kind as Server["kind"],
        discovery: typeof r.discovery === "string" ? r.discovery : "",
        web,
      },
    ];
  });
}

/**
 * The server this app is on, "You are here" (WEB-SRV-LST-02): the one entry of this environment's kind; with several
 * of that kind, the one whose `web` is this origin; otherwise none, since a region can live on another origin
 * (SRV-06, SRV-08).
 */
export function currentServer(
  servers: readonly Server[],
  environment: AppEnvironment,
  origin: string,
): Server | undefined {
  const same = servers.filter((s) => s.kind === KIND_OF[environment]);
  if (same.length === 1) return same[0];
  return same.find((s) => s.web === origin);
}

export const serversQuery = () =>
  queryOptions({
    queryKey: ["servers.json"],
    queryFn: async () => {
      const res = await fetch(SERVERS_PATH, { cache: "no-cache" });
      if (!res.ok) throw new Error(`servers.json: HTTP ${res.status}`);
      return parseServers(await res.json());
    },
    staleTime: 24 * 60 * 60_000,
    retry: 1,
  });
